import { useEffect, useRef, useState } from "react";
import React from "react";
import { TriggeredHook } from "sonobello.utilities.react";
import { Nullable } from "sonobello.utilities.react.mui";

import { SessionContextProps } from "../../../../../Context/SessionContext";
import { UserContextProps } from "../../../../../Context/UserContext";
import { CustomDimension } from "../../../../../Models/CustomDimension";
import { LeadResult } from "../../../../../Models/Lead";
import { LeadExternalSystem } from "../../../../../Utils/Enums";
import EnvironmentConfiguration from "../../../../Constants/EnvironmentConfiguration";
import LeadResultType from "../../../../Constants/LeadResultType";
import ILegacyCreateOrUpdateContactResultRequest from "../../Types/ILegacyCreateOrUpdateContactResultRequest";
import { IDispositionStepConfirmationModal } from "../DispositionStep";
import { IDispositionConfirmationFormProps } from "./ConfirmationForm";
import DispositionModal from "./DispositionModal";

export interface ISessionDispositionModal
  extends IDispositionStepConfirmationModal,
    Pick<
      SessionContextProps,
      "customer" | "ignoreNonCan" | "lead" | "leadResult" | "setLeadResult" | "setCustomerInfo"
    >,
    Pick<UserContextProps, "leadResultTypes" | "ivrState">,
    Pick<IDispositionConfirmationFormProps, "formMap"> {
  /** The sessions newly created appointment if any. */
  appointment: SessionContextProps["appointment"];
  /** The reasons for which the current session's guest is not a candidate for our services. */
  disqualificationReasons: SessionContextProps["leadDisqualifiers"];
  /** The customer that was selected during the Search step. */
  selectedCustomer: SessionContextProps["selectedCustomer"];
  /** The id of the user. */
  userId: UserContextProps["user"]["id"];
  /** The callback which ends the current session when executed. */
  endSession: SessionContextProps["helpers"]["endSession"];
  /** The hook for creating or updating a lead result. */
  usePutPostLeadResult: TriggeredHook<
    LeadResult,
    ILegacyCreateOrUpdateContactResultRequest,
    ILegacyCreateOrUpdateContactResultRequest
  >;
}

/**
 * An extension of the base {@link DispositionModal} which adds additional context from the session including the
 * current user, customer, etc.
 */
const SessionDispositionModal: React.FC<ISessionDispositionModal> = ({
  appointment,
  customer,
  disqualificationReasons,
  ignoreNonCan,
  ivrState,
  lead,
  leadResult,
  leadResultType,
  leadResultTypes,
  formMap,
  selectedCustomer,
  userId,
  endSession,
  handleConfirm,
  setCustomerInfo,
  setLeadResult,
  usePutPostLeadResult,
  ...rest
}) => {
  const [confirmDisabledReason, setConfirmDisabledReason] = useState<Nullable<string>>(null);
  const [leadResultProps, setLeadResultProps] = useState<LeadResult>({
    id: leadResult?.id,
    type: leadResultType.id,
    customerId: customer.id,
    appointmentId: appointment?.id || null,
    opportunityId: customer.opportunity?.id || null,
    disqualifyingReasons: ignoreNonCan ? {} : CustomDimension.toObject(disqualificationReasons, true),
    notes: leadResult?.notes || null,
    notOfferedProcedures: leadResult?.notOfferedProcedures ? { ...leadResult.notOfferedProcedures } : {}
  });
  const [saveLeadResult, setSaveLeadResult] = useState<LeadResult>();

  useEffect(() => {
    setLeadResultProps({
      id: leadResult?.id,
      type: leadResultType.id,
      customerId: customer.id,
      appointmentId: appointment?.id || null,
      opportunityId: customer.opportunity?.id || null,
      disqualifyingReasons: ignoreNonCan ? {} : CustomDimension.toObject(disqualificationReasons, true),
      notes:
        leadResultType.id === EnvironmentConfiguration.leadResultIds[LeadResultType.OtherProcedures]
          ? leadResult?.notes || null
          : null,
      notOfferedProcedures: leadResult?.notOfferedProcedures || {}
    });
  }, [
    leadResult,
    leadResultType.id,
    customer.id,
    appointment?.id,
    customer.opportunity?.id,
    ignoreNonCan,
    disqualificationReasons
  ]);

  const deleteOpportunityRef = useRef(false);

  useEffect(() => {
    deleteOpportunityRef.current =
      // we proceeded with an existing customer
      (selectedCustomer &&
        // AND that customer did not NOT initially have an opportunity
        // (we created a new opportunity for the customer during the session)
        !selectedCustomer.opportunity?.id &&
        // AND we are dispositioning as existing patient
        leadResultTypes.find(lrt => lrt.id === saveLeadResult?.type)?.id ===
          EnvironmentConfiguration.leadResultIds[LeadResultType.ExistingPatient]) ||
      false;
  }, [saveLeadResult, selectedCustomer, leadResultTypes]);

  const { execute, result: response, error, isLoading } = usePutPostLeadResult();

  useEffect(() => {
    if (saveLeadResult) {
      execute({
        leadId: lead.id,
        leadResult: saveLeadResult,
        shouldDeleteOpportunity: deleteOpportunityRef.current
      });
    }
  }, [saveLeadResult]);

  // ON PUT/POST LEAD RESULT SUCCESS
  useEffect(() => {
    if (response) {
      // save the response
      setLeadResult(response);

      // if we flagged the opportunity for deletion, clear it
      if (deleteOpportunityRef.current) {
        setCustomerInfo({
          opportunity: {
            campaign: lead.campaign,
            centerId: customer.centerId || undefined,
            createdByUserId: userId
          }
        });
        // force a second render to end the session after the opportunity is deleted on the customer.
        setTimeout(() => endSession(), 0);
      } else endSession();
    }
  }, [
    response,
    customer.centerId,
    lead.campaign,
    saveLeadResult,
    selectedCustomer,
    userId,
    endSession,
    setCustomerInfo,
    setLeadResult,
    customer
  ]);

  // ON PUT/POST LEAD RESULT ERROR
  useEffect(() => {
    if (!error) return;
    setLeadResult(error.leadResult);
    handleConfirm();
    endSession();
  }, [error, endSession, setLeadResult]);

  return DispositionModal({
    confirmDisabled: Boolean(confirmDisabledReason),
    confirmSpinner: isLoading,
    confirmDisabledTooltip: confirmDisabledReason || undefined,
    isLoading,
    leadResultType,
    formProps: {
      isConfirmed: Boolean(saveLeadResult),
      leadResultProps,
      leadResultType,
      formMap,
      showIvrEmailWarning:
        lead.externalSystem === LeadExternalSystem.Email &&
        (ivrState.state !== "Unavailable" || ivrState.reason !== "Wrap Up"),
      summaryAddendum: null,
      setConfirmDisabledReason,
      setLeadResultProps
    },
    handleConfirm: () => setSaveLeadResult(leadResultProps),
    ...rest
  });
};

export default SessionDispositionModal;
